import { useState, useEffect, useRef } from "react";
import { DISTRIBUTION_SERVICES } from "@/data/distributionServices";
import { PLATFORM_ICON_MAP } from "@/components/episode/PlatformIcons";

const platforms = DISTRIBUTION_SERVICES.map(service => ({
  id: service.id,
  name: service.name,
  icon: PLATFORM_ICON_MAP[service.id] ?? null,
}));

const VISIBLE_MOBILE = 2;
const VISIBLE_TABLET = 3;
const VISIBLE_DESKTOP = 4;
const ROTATE_INTERVAL_MS = 2500;

/** Fixed pill + gap widths. Every slot is identical, so rotating the track can
 *  never change the strip's size — the old variable-width pills nudged the
 *  surrounding rows on every swap and accrued layout shift indefinitely. */
const PILL_W = 176;
const GAP = 12;
const SLOT = PILL_W + GAP;

export function PlatformLogosCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Rotate only while the strip is on screen and the visitor hasn't asked for
  // reduced motion. Icons load lazily per slot — pre-fetching all 36 SVGs up
  // front competed with the first paint on phones.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let interval: ReturnType<typeof setInterval> | undefined;

    const start = () => {
      if (interval) return;
      interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % platforms.length);
      }, ROTATE_INTERVAL_MS);
    };

    const stop = () => {
      if (interval) clearInterval(interval);
      interval = undefined;
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) start();
      else stop();
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
      stop();
    };
  }, []);

  // Render one extra slot so the outgoing pill slides out rather than popping.
  const slots = Array.from({ length: VISIBLE_DESKTOP }, (_, i) =>
    platforms[(currentIndex + i) % platforms.length]
  );

  return (
    <div ref={containerRef} className="flex justify-center my-6 md:my-8">
      {/* Fixed-width window sized to 2/3/4 identical pills. Nothing here can
          change width, so the strip never nudges the rows around it. */}
      {/* Project breakpoints: sm = tablet (768), md = desktop (1200). */}
      <div className="overflow-hidden w-[364px] sm:w-[552px] md:w-[740px]">
        <div className="flex" style={{ gap: `${GAP}px` }}>
          {slots.map((platform, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-secondary/10 border border-secondary/20"
              style={{ flex: `0 0 ${PILL_W}px`, width: PILL_W }}
            >
              <span className="flex-shrink-0">
                {platform.icon ? (
                  <img
                    src={platform.icon}
                    alt=""
                    role="presentation"
                    width={24}
                    height={24}
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded"
                    loading={index < VISIBLE_MOBILE ? "eager" : "lazy"}
                    decoding="async"
                  />
                ) : (
                  <span aria-hidden="true" className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-neutral/20 flex items-center justify-center text-[10px] font-bold text-secondary/60">
                    {platform.name.charAt(0)}
                  </span>
                )}
              </span>
              <span className="truncate text-xs sm:text-sm text-secondary/80 font-medium">
                {platform.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
